import { TeacherMetricCard, TeacherSectionHeader, TeacherStudentResultRow, TeacherAssessmentHistoryRow, TeacherDrawerShell, TeacherDrawerNav } from '../../design/TeacherComponents';
import React, { useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import { classPulseDate, classPulseFormat } from './classPulseMetrics';
import { CourseGlyph, getCourseVisual } from './ClassPulseVisualTokens';
import { subjectChartPoints } from './classPulseSubjectDetail';
import { teacherTypography, teacherChartText } from '../../design/typography';
import { DEVELOPMENT_LEVELS, developmentLevelLabel } from '../../shared/developmentLevels';

const focusStyle = {
  minHeight: 44, minWidth: 44,
  '&.Mui-focusVisible': { outline: '3px solid #302479', outlineOffset: 2 },
};
const cardStyle = {
  borderRadius: 3.2, borderColor: '#e5e6f1',
  background: 'linear-gradient(120deg,#fff,#fdfcff)',
};

const StatCard = TeacherMetricCard;

function ExamHistoryChart({ exams, label }) {
  const points = useMemo(() => subjectChartPoints(exams), [exams]);
  const distinctDates = new Set(points.map(point => point.date)).size;
  if (distinctDates < 2) {
    return (
      <Typography variant="body2" sx={{ ...teacherTypography.supportingText, py: 3 }}>
        En az iki farklı tarihte puanlı değerlendirme olduğunda sınav ortalamaları grafiği görünür.
        Aşağıdaki değerlendirme listesi yine incelenebilir.
      </Typography>
    );
  }
  const plotted = points.slice(-8);
  const x = idx => 43 + 510 * idx / (plotted.length - 1);
  const y = score => 154 - score * 1.15;
  const line = plotted.map((p, index) => `${x(index)},${y(p.average)}`).join(' ');
  const area = `M ${x(0)} 154 L ${line.replaceAll(' ', ' L ')} L ${x(plotted.length - 1)} 154 Z`;
  const visual = getCourseVisual(label);
  return (
    <Box sx={teacherChartText}>
      <svg role="img" viewBox="0 0 600 202" style={{ display: 'block', width: '100%', height: 'auto' }}
        aria-label={`${label} sınav ortalamaları: ${plotted.map(p => `${classPulseDate(p.date)} ${classPulseFormat(p.average)}`).join(', ')}`}>
        <defs>
          <linearGradient id="danduV5HistoryFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={visual.line} stopOpacity="0.22"/>
            <stop offset="100%" stopColor={visual.line} stopOpacity="0.015"/>
          </linearGradient>
        </defs>
        {[0, 25, 50, 75, 100].map(value => (
          <g key={value}>
            <line x1="37" x2="562" y1={y(value)} y2={y(value)}
              stroke="#e7e8f2" strokeDasharray="5 5"/>
            <text className="dandu-chart-axis" x="4" y={y(value) + 4}>{value}</text>
          </g>
        ))}
        <path data-testid="v5-exam-chart-fill" d={area} fill="url(#danduV5HistoryFill)" />
        <polyline points={line} stroke={visual.line} strokeWidth="3.5" fill="none"
          strokeLinecap="round" strokeLinejoin="round"/>
        {plotted.map((point, index) => (
          <g key={point.id}>
            <circle cx={x(index)} cy={y(point.average)} r="5" fill="#fff"
              stroke={visual.line} strokeWidth="3"/>
            <text className="dandu-chart-score" x={x(index)} y={Math.max(13, y(point.average) - 13)}
              textAnchor="middle">
              {classPulseFormat(point.average)}
            </text>
            <text className={plotted.length > 4 && index % 2 === 1
              ? 'dandu-chart-date dandu-chart-date-optional' : 'dandu-chart-date'}
              x={x(index)} y="181" textAnchor="middle">
              {point.date.slice(5).split('-').reverse().join('.')}
            </text>
          </g>
        ))}
      </svg>
      <Typography variant="caption" sx={{ ...teacherTypography.metadata, display: 'block', lineHeight: 1.6 }}>
        {distinctDates < 3
          ? 'İki tarihli sınav ortalamaları gösteriliyor; öğrenme eğilimi hakkında yorum yapılmaz.'
          : 'Sınav ortalamaları sınavların kapsam ve zorluk farkları nedeniyle tek başına öğrenmedeki değişimi kanıtlamaz.'}
        {points.length > 8 ? ' Grafikte son sekiz sınav yer alır; tüm sonuçlar listelenir.' : ''}
      </Typography>
    </Box>
  );
}

function SubjectOverview({ subject, onSelectExam }) {
  return (
    <>
      <Stack direction="row" alignItems="center" spacing={1.6} sx={{ mb: 2.5 }}>
        <CourseGlyph name={subject.label} size={64}/>
        <Box sx={{ minWidth: 0 }}>
          <Typography component="h2" variant="h6" sx={teacherTypography.sectionTitle}>{subject.label}</Typography>
          <Typography variant="body2" sx={teacherTypography.supportingText}>Dersin Nabzı · Sınıf değerlendirmeleri</Typography>
        </Box>
      </Stack>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2,minmax(0,1fr))',
        sm: 'repeat(3,minmax(0,1fr))' }, gap: 1.1, mb: 1.5 }}>
        <StatCard density="report" emphasis label="Ders ortalaması" value={classPulseFormat(subject.average)}
          help="/100 · öğrenci ağırlıklı"/>
        <StatCard density="report" label="Değerlendirme" value={String(subject.examCount)} help="seçili kapsam"/>
        <Box sx={{ gridColumn: { xs: '1 / -1', sm: 'auto' } }}>
          <StatCard density="report" label="Puanlı öğrenci" value={String(subject.scoredStudents)}
            help={`${subject.totalStudents} öğrenciden`}/>
        </Box>
      </Box>
      <Paper variant="outlined" sx={{ ...cardStyle, bgcolor: '#f6f8ff', p: 1.6, mb: 2,
        borderColor: '#dfe7ff' }}>
        <Stack direction="row" spacing={1} alignItems="flex-start">
          <InfoOutlinedIcon sx={{ color: '#6188d0', mt: 0.15 }} fontSize="small"/>
          <Typography variant="body2" sx={{ lineHeight: 1.65 }}>
            Ders ortalaması öğrencilerin ders puan ortalamalarından eşit ağırlıkla hesaplanır.
            Grafikte ise <strong>her sınavın puanlı sonuç ortalaması</strong> gösterilir.
            Girmedi ve bekleyenler sıfır sayılmaz.
          </Typography>
        </Stack>
      </Paper>
      <Paper variant="outlined" sx={{ ...cardStyle, p: { xs: 1.5, sm: 2.3 }, mb: 2 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.4 }}>
          <CourseGlyph name={subject.label} size={33}/>
          <TeacherSectionHeader level="h3" title="Sınıfın Sınav Geçmişi" />
        </Stack>
        <ExamHistoryChart exams={subject.exams} label={subject.label}/>
        <Divider sx={{ my: 2 }}/>
        <TeacherSectionHeader level="h3" title="Geçmiş Değerlendirmeler
        " />
        {subject.exams.length ? (
          <Stack>
            {subject.exams.map(exam => (
              <TeacherAssessmentHistoryRow key={exam.id} title={exam.title} dateLabel={classPulseDate(exam.date)}
                metadata={(exam.scoringMode === 'development' ? exam.evaluatedCount + ' düzey sonucu' : exam.scoredCount + ' puanlı') + ' · ' + exam.absentCount + ' girmedi'}
                value={exam.scoringMode === 'development' ? null : exam.average}
                summary={exam.scoringMode === 'development' ? 'Düzey sonucu' : null}
                actionLabel={exam.title + ' sınavının öğrenci sonuçlarını aç'} onClick={() => onSelectExam(exam.id)} />
            ))}
          </Stack>
        ) : (
          <Typography variant="body2" sx={{ ...teacherTypography.supportingText, py: 2 }}>
            Bu filtre kapsamında değerlendirme bulunamadı.
          </Typography>
        )}
      </Paper>
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, bgcolor: '#f7f4ff',
        borderColor: '#e4d9ff' }}>
        <Stack direction="row" spacing={1} alignItems="flex-start">
          <AutoAwesomeOutlinedIcon fontSize="small" sx={{ color: '#7255d9', mt: 0.2 }}/>
          <Box>
            <Typography variant="subtitle2" fontWeight={600}>Öğretmen için kısa bakış</Typography>
            <Typography variant="body2" sx={{ mt: 0.6, lineHeight: 1.65 }}>
              Bu dersin sınav ortalamalarını ve puan girilen öğrenci sayılarını birlikte inceleyebilirsiniz.
              Ayrıntılar için bir değerlendirme seçin. Farklı sınavlar doğrudan eşdeğer kabul edilmemelidir.
            </Typography>
          </Box>
        </Stack>
      </Paper>
    </>
  );
}

function ExamResults({ subject, exam, onBack }) {
  return (
    <>
      <Button variant="text" startIcon={<ArrowBackIcon/>} onClick={onBack}
        sx={{ ...focusStyle, mb: 1, textTransform: 'none', justifyContent: 'flex-start', fontWeight: 700 }}>
        {subject.label} dersine dön
      </Button>
      <Stack direction="row" spacing={1.4} alignItems="center" sx={{ mb: 2 }}>
        <CourseGlyph name={subject.label} size={58}/>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h6" component="h2" sx={{ ...teacherTypography.sectionTitle, overflowWrap: 'anywhere' }}>
            {exam.title}
          </Typography>
          <Typography variant="body2" sx={teacherTypography.supportingText}>
            {subject.label} · {classPulseDate(exam.date)}
          </Typography>
        </Box>
      </Stack>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2,minmax(0,1fr))',
        sm: 'repeat(3,minmax(0,1fr))' }, gap: 1, mb: 2 }}>
        {exam.scoringMode === 'development'
          ? <StatCard density="report" label="Gelişim Düzeyi" value={String(exam.evaluatedCount)} help="değerlendirildi" emphasis/>
          : <StatCard density="report" label="Sınav ortalaması" value={classPulseFormat(exam.average)} help="/100 · puanlı sonuç" emphasis/>}
        <StatCard density="report" label={exam.scoringMode === 'development' ? 'Bekleyen öğrenci' : 'Puanlı öğrenci'}
          value={String(exam.scoringMode === 'development' ? exam.pendingCount : exam.scoredCount)} help={`${subject.totalStudents} öğrenciden`}/>
        <Box sx={{ gridColumn: { xs: '1 / -1', sm: 'auto' } }}>
          <StatCard density="report" label={exam.scoringMode === 'development' ? 'Katılmadı' : 'Eksik sonuç'} value={String(exam.scoringMode === 'development' ? exam.absentCount : exam.absentCount + exam.pendingCount)}
            help={`${exam.absentCount} girmedi · ${exam.pendingCount} bekliyor`}/>
        </Box>
      </Box>
      <Paper variant="outlined" sx={{ ...cardStyle, p: 1.6, mb: 2,
        bgcolor: '#f6f8ff', borderColor: '#dfe7ff' }}>
        <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
          {exam.scoringMode === 'development' ? 'Gelişim düzeyleri kategorik sonuçlardır; puana dönüştürülmez ve ortalamaya katılmaz.' : <>Gerçek <strong>0</strong> puan ortalamaya dahildir. “Girmedi” ve “Sonuç bekleniyor” kayıtları ortalamaya dahil değildir.</>}
        </Typography>
      </Paper>
      {exam.scoringMode === 'development' && <Box aria-label="Düzey dağılımı" sx={{ display: 'grid',
        gridTemplateColumns: { xs: 'repeat(2,minmax(0,1fr))', sm: 'repeat(4,minmax(0,1fr))' }, gap: 1, mb: 2 }}>
        {DEVELOPMENT_LEVELS.map(level => <StatCard density="report" key={level.code} label={level.label}
          value={String(exam.distribution[level.code])} help="öğrenci" />)}
      </Box>}
      <Paper variant="outlined" sx={{ ...cardStyle, p: { xs: 1.4, sm: 2 } }}>
        <TeacherSectionHeader level="h3" title="Öğrenci Sonuçları
        " />
        <Stack>
          {exam.students.map(student => (
            <TeacherStudentResultRow key={student.id} student={student} value={student.status === 'completed' && !student.developmentLevel ? student.score : null}
              summary={student.developmentLevel ? developmentLevelLabel(student.developmentLevel) : student.status === 'absent' ? 'Girmedi' : student.status === 'pending' ? 'Sonuç bekleniyor' : null} />
          ))}
        </Stack>
      </Paper>
      <Button onClick={onBack} variant="outlined" fullWidth startIcon={<ArrowBackIcon/>}
        sx={{ ...focusStyle, mt: 2, textTransform: 'none' }}>
        {subject.label} sınavlarına dön
      </Button>
    </>
  );
}

export default function ClassPulseSubjectDrawer({ subject, className, onClose }) {
  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [examId, setExamId] = useState(null);
  useEffect(() => { setExamId(null); }, [subject?.key]);
  const exam = subject?.exams.find(item => item.id === examId) || null;
  return (
    <TeacherDrawerShell open={Boolean(subject)} onClose={onClose}
      label={subject ? `${subject.label} dersinin değerlendirme geçmişi` : undefined}>
      {subject && (
        <Box sx={{ p: { xs: 2, sm: 3 }, minWidth: 0, pb: 4, overflowY: 'auto', minHeight: '100%' }}>
          <TeacherDrawerNav onClose={onClose} back={mobile} label={mobile ? 'Ders listesine geri dön' : 'Ders detayını kapat'} context={className + ' · Dersin Nabzı'} />
          {exam ? (
            <ExamResults key={exam.id} subject={subject} exam={exam} onBack={() => setExamId(null)}/>
          ) : (
            <SubjectOverview subject={subject} onSelectExam={setExamId}/>
          )}
        </Box>
      )}
    </TeacherDrawerShell>
  );
}
